

class ShopifyLicenseValidator {
    constructor(options = {}) {
        this.licenseServer = options.licenseServer || window.THEME_LICENSE_CONFIG?.serverUrl || 'https://portal.surgetheme.com';
        this.licenseKey = null;
        this.storeUrl = null; 
        this.isValid = false;
        this.licenseStatus = 'unknown'; 
        this.domain = null; 
        this.checkInterval = options.checkInterval || 24 * 60 * 60 * 1000; 
        this.retryAttempts = options.retryAttempts || 3;
        this.cacheDuration = options.cacheDuration || 0; 
        
        
        this.hasImmediateStatus = false;
        this.immediateStatusApplied = false;
        
      
        this.debugMode = options.debugMode || window.THEME_LICENSE_DEBUG || false;
        
      
        this.boundLicenseClickHandler = this.licenseClickHandler.bind(this);
        
        this.init();
    }
    
   
    log(message, data = null) {
        if (!this.debugMode) return;
        if (data) this.log('[DEBUG] ' + message, data);
        else this.log('[DEBUG] ' + message);
    }
    
   
    logError(message) {
    }

    generateSettingsHash(licenseKey, storeUrl) {
        const combined = `${licenseKey || 'none'}_${storeUrl || 'none'}`;
        let hash = 0;
        for (let i = 0; i < combined.length; i++) {
            const char = combined.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
        }
        return hash.toString();
    }

    init() {
        this.log(' Initializing...');
        
       
        this.licenseKey = this.getLicenseFromSettings();
        this.storeUrl = this.getStoreUrlFromSettings();
        
     
        if (this.storeUrl) {
           
            if (!this.validateStoreUrl(this.storeUrl)) {
                this.storeUrl = null;
                this.domain = null;
            } else {
                this.domain = this.extractDomainFromUrl(this.storeUrl);
                this.log('Domain extracted');
            }
        }
        
        this.log('Init status check');
        
       
        this.setupDOMObserver();
        
        const currentSettingsHash = this.generateSettingsHash(this.licenseKey, this.storeUrl);
        const previousSettingsHash = localStorage.getItem('lastSettingsHash');
        
        if (currentSettingsHash !== previousSettingsHash) {
            this.log(' Settings changed detected');
            this.settingsChanged = true;
            localStorage.setItem('lastSettingsHash', currentSettingsHash);
        } else {
            this.log(' No settings change - same license and store URL');
            this.settingsChanged = false;
        }
        
        if (this.licenseKey && this.storeUrl) {
            this.log(' Both license key and store URL found - starting background validation');
            
            
            this.showValidationLoading();
            
            
            setTimeout(() => {
                this.log(' Performing background license validation...');
                this.validateLicense();
            }, 50);

            return;
        }
        
        if (this.licenseKey && !this.storeUrl) {
            this.log(' License key found but store URL missing');
        }
        
        
        this.log(' Missing required fields - applying restrictions');
        
      
        if (window.Shopify?.designMode || window.location.href.includes('/customize')) {
            this.showCustomizerOverlay();
        }
        
        this.licenseStatus = 'invalid';
        this.isValid = false;
        this.disablePurchaseButtons();
        
        localStorage.setItem('lastLicenseState', 'invalid');
        localStorage.setItem('licenseStatus', 'invalid');
        localStorage.setItem('isLicenseValid', 'false');
        return;
    }

    showValidationLoading() {
       
        if (!window.Shopify?.designMode && !window.location.href.includes('/customize')) {
            return;
        }
        
        const loading = document.createElement('div');
        loading.id = 'license-validation-loading';
        loading.style.cssText = `
            position: fixed;
            top: 10px;
            right: 10px;
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 12px;
            z-index: 9999;
            display: flex;
            align-items: center;
            gap: 8px;
        `;
        
        loading.innerHTML = `
            <div style="width: 12px; height: 12px; border: 2px solid white; border-top-color: transparent; border-radius: 50%; animation: spin 0.8s linear infinite;"></div>
            <span>Verifying license...</span>
            <style>
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            </style>
        `;
        
        document.body.appendChild(loading);
    }

    hideValidationLoading() {
        const loading = document.getElementById('license-validation-loading');
        if (loading) {
            loading.style.opacity = '0';
            loading.style.transition = 'opacity 0.3s';
            setTimeout(() => loading.remove(), 300);
        }
    }

    showCustomizerOverlay() {
        const overlay = document.getElementById('license-required-overlay');
        if (overlay) {
            overlay.style.setProperty('display', 'block', 'important');
            
            const existingHelper = overlay.querySelector('.domain-helper-message');
            if (!existingHelper && this.licenseKey && !this.storeUrl) {
                let detectedDomain = null;
                if (window.themePrimaryDomain) {
                    detectedDomain = window.themePrimaryDomain;
                } else if (window.Shopify?.shop) {
                    detectedDomain = window.Shopify.shop;
                } else if (window.location?.hostname) {
                    detectedDomain = window.location.hostname;
                }
                
                if (detectedDomain) {
                    const helperMessage = document.createElement('div');
                    helperMessage.className = 'domain-helper-message';
                    helperMessage.style.cssText = `
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        color: white;
                        padding: 14px;
                        border-radius: 10px;
                        margin-top: 14px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                        text-align: left;
                        box-sizing: border-box;
                    `;
                    
                    helperMessage.innerHTML = `
                        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0;">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="12" y1="8" x2="12" y2="12"/>
                                <line x1="12" y1="16" x2="12.01" y2="16"/>
                            </svg>
                            <strong style="font-size: 13px;">Store URL Required</strong>
                        </div>
                        <p style="margin: 0 0 8px 0; font-size: 11px; line-height: 1.5;">
                            Please enter your correct Store URL:
                        </p>
                        <div style="background: rgba(255,255,255,0.2); padding: 8px; border-radius: 6px; margin-bottom: 8px; display: flex; align-items: center; justify-content: space-between; gap: 6px; flex-wrap: wrap; box-sizing: border-box;">
                            <code style="font-size: 11px; font-weight: bold; letter-spacing: 0.3px; word-break: break-all; flex: 1; min-width: 100px;">${detectedDomain}</code>
                            <button id="copy-domain-helper-btn" style="background: rgba(255,255,255,0.3); color: white; border: none; padding: 4px 10px; border-radius: 5px; cursor: pointer; font-size: 10px; font-weight: 600; white-space: nowrap; flex-shrink: 0;">Copy</button>
                        </div>
                        <ol style="margin: 0; padding-left: 16px; font-size: 10px; line-height: 1.5;">
                            <li style="margin-bottom: 4px;">Copy the domain above</li>
                            <li style="margin-bottom: 4px;">Paste it in "Shopify Store URL" field</li>
                            <li>Click Save button</li>
                        </ol>
                    `;
                    
                    const licenseBox = overlay.querySelector('.license-notice-box');
                    if (licenseBox) {
                        licenseBox.appendChild(helperMessage);
                    } else {
                        const overlayContent = overlay.querySelector('.license-overlay-content');
                        if (overlayContent) {
                            overlayContent.appendChild(helperMessage);
                        } else {
                            overlay.appendChild(helperMessage);
                        }
                    }
                    
                    const copyBtn = document.getElementById('copy-domain-helper-btn');
                    if (copyBtn) {
                        copyBtn.addEventListener('click', async function() {
                            try {
                                await navigator.clipboard.writeText(detectedDomain);
                                this.textContent = '✓ Copied';
                                this.style.background = '#10b981';
                                setTimeout(() => {
                                    this.textContent = 'Copy';
                                    this.style.background = 'rgba(255,255,255,0.3)';
                                }, 2000);
                            } catch (err) {
                                const tempInput = document.createElement('input');
                                tempInput.value = detectedDomain;
                                document.body.appendChild(tempInput);
                                tempInput.select();
                                document.execCommand('copy');
                                document.body.removeChild(tempInput);
                                this.textContent = '✓ Copied';
                                this.style.background = '#10b981';
                                setTimeout(() => {
                                    this.textContent = 'Copy';
                                    this.style.background = 'rgba(255,255,255,0.3)';
                                }, 2000);
                            }
                        });
                    }
                }
            }
        }
    }

    hideCustomizerOverlay() {
        const overlay = document.getElementById('license-required-overlay');
        if (overlay) {
            overlay.style.setProperty('display', 'none', 'important');
        }
    }

    setupDOMObserver() {
       
        try {
            
            if (this.domObserver) {
                this.domObserver.disconnect();
            }
            
            let refreshTimeout;
            const observer = new MutationObserver((mutations) => {
               
                if (refreshTimeout) return;
                
                let shouldRefresh = false;
                
                mutations.forEach((mutation) => {
                    if (mutation.addedNodes.length > 0) {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === Node.ELEMENT_NODE) {
                                try {
                                   
                                    const hasButtons = node.matches && (
                                        node.matches('button[type="submit"][name="add"], .btn-cart, .add-to-cart, .buy-now, .btn-buy-now') ||
                                        node.querySelector('button[type="submit"][name="add"], .btn-cart, .add-to-cart, .buy-now, .btn-buy-now, .shopify-payment-button')
                                    );
                                    
                                    if (hasButtons) {
                                        shouldRefresh = true;
                                    }
                                } catch (e) {
                                    
                                }
                            }
                        });
                    }
                });
                
                if (shouldRefresh) {
                    
                    refreshTimeout = setTimeout(() => {
                        try {
                            this.refreshRestrictions();
                        } catch (e) {
                            this.log('DOM refresh error:', e.message);
                        }
                        refreshTimeout = null;
                    }, 200);
                }
            });
            
           
            observer.observe(document.body, {
                childList: true,
                subtree: false 
            });
            
            this.domObserver = observer;
            
        } catch (e) {
            this.log('DOM Observer setup failed:', e.message);
          
        }
    }

    getLicenseFromSettings() {
      
        let licenseKey = null;
        
      
        if (window.THEME_LICENSE_CONFIG && window.THEME_LICENSE_CONFIG.licenseKey) {
            licenseKey = window.THEME_LICENSE_CONFIG.licenseKey.trim();
        }
        
      
        if (!licenseKey && window.themeLicenseKey) {
            licenseKey = window.themeLicenseKey.trim();
        }
        
       
        if (!licenseKey) {
            const stored = localStorage.getItem('themeLicenseKey');
            if (stored) {
                licenseKey = stored.trim();
            }
        }
        
       
        if (!licenseKey || licenseKey.length < 10 || !/^[A-Z0-9\-]+$/i.test(licenseKey)) {
            return null;
        }
        
        return licenseKey;
    }

    getStoreUrlFromSettings() {
       
        let storeUrl = null;
        

        if (window.THEME_LICENSE_CONFIG && window.THEME_LICENSE_CONFIG.storeUrl) {
            storeUrl = window.THEME_LICENSE_CONFIG.storeUrl.trim();
        }
        
      
        if (!storeUrl && window.themeStoreUrl) {
            storeUrl = window.themeStoreUrl.trim();
        }
        
  
        if (!storeUrl) {
            const stored = localStorage.getItem('themeStoreUrl');
            if (stored) {
                storeUrl = stored.trim();
            }
        }
        
      
        if (!storeUrl || storeUrl.length < 5) {
            return null;
        }
        
        return storeUrl;
    }

    validateStoreUrl(url) {
        if (!url) return false;
        
    
        const cleanUrl = url.replace(/^https?:\/\/(www\.)?/, '').toLowerCase();
        
      
        const isShopifyUrl = cleanUrl.endsWith('.myshopify.com');
        
      
        if (isShopifyUrl) {
            this.log(' Valid Shopify URL:', cleanUrl);
            return true;
        }
        
   
        const domainParts = cleanUrl.split('.');
        if (domainParts.length < 2) {
            this.log(' Invalid domain format - missing extension:', cleanUrl);
            return false;
        }
        
 
        const isValidDomainFormat = /^[a-zA-Z0-9][a-zA-Z0-9-_.]*$/.test(domainParts[0]);
        
        this.log(' Store URL Validation:', {
            url: url,
            cleanUrl: cleanUrl,
            domainParts: domainParts.length,
            isValidFormat: isValidDomainFormat,
            isShopify: isShopifyUrl
        });
        
        if (!isValidDomainFormat) {
            this.log(' Invalid domain format:', cleanUrl);
            return false;
        }
        
        this.log(' Valid URL format:', cleanUrl);
        return true;
    }
    
    cleanUrlForComparison(url) {
        if (!url) return '';
        
     
        let clean = url.toLowerCase();
        
        
        clean = clean.replace(/^https?:\/\//, '');
        
       
        clean = clean.replace(/^www\./, '');
        
       
        clean = clean.split('/')[0];
        
       
        clean = clean.split(':')[0];
        
       
        
        return clean;
    }

  
    getCurrentStoreDomain() {
        let currentDomain = null;
        
        if (window.Shopify && window.Shopify.shop) {
            currentDomain = window.Shopify.shop;
            this.log(' Store domain from Shopify.shop:', currentDomain);
        }
        else if (window.location && window.location.hostname) {
            currentDomain = window.location.hostname;
            this.log(' Store domain from location.hostname:', currentDomain);
        }
        else if (document.location && document.location.hostname) {
            currentDomain = document.location.hostname;
            this.log(' Store domain from document.location:', currentDomain);
        }
        
        if (!currentDomain) {
            this.log(' Could not detect current store domain');
            return null;
        }
        
        const cleanDomain = this.extractDomainFromUrl(currentDomain);
        this.log(' Current store domain detected:', cleanDomain);
        return cleanDomain;
    }

    getCurrentStoreDomains() {
        const domains = [];
        
        if (window.Shopify && window.Shopify.shop) {
            const shopifyDomain = this.extractDomainFromUrl(window.Shopify.shop);
            if (shopifyDomain) {
                domains.push(shopifyDomain);
                this.log(' Original Shopify domain:', shopifyDomain);
            }
        }
        
        if (window.location && window.location.hostname) {
            const currentDomain = this.extractDomainFromUrl(window.location.hostname);
            if (currentDomain && !domains.includes(currentDomain)) {
                domains.push(currentDomain);
                this.log(' Current visiting domain:', currentDomain);
            }
        }
        
        if (document.location && document.location.hostname) {
            const docDomain = this.extractDomainFromUrl(document.location.hostname);
            if (docDomain && !domains.includes(docDomain)) {
                domains.push(docDomain);
                this.log(' Document domain:', docDomain);
            }
        }
        
        this.log(' All detected store domains:', domains);
        return domains;
    }

    validateStoreUrlMatch(enteredUrl) {
        if (!enteredUrl) {
            this.log(' No URL provided for validation');
            return false;
        }
        
        const isValidFormat = this.validateStoreUrl(enteredUrl);
        if (!isValidFormat) {
            this.log(' Invalid URL format:', enteredUrl);
            return false;
        }
        
        const currentDomains = this.getCurrentStoreDomains();
        if (!currentDomains || currentDomains.length === 0) {
            this.log(' Could not detect current store domains - will rely on server validation');
            return true;
        }
        
        const enteredDomain = this.extractDomainFromUrl(enteredUrl);
        if (!enteredDomain) {
            this.log(' Could not extract domain from entered URL:', enteredUrl);
            return false;
        }
        
        const domainsMatch = currentDomains.includes(enteredDomain);
        
        this.log(' Domain Match Validation (Custom Domain Support):', {
            enteredUrl: enteredUrl,
            currentDomains: currentDomains,
            enteredDomain: enteredDomain,
            match: domainsMatch
        });
        
        if (!domainsMatch) {
            this.log(' Domain mismatch detected - but allowing for custom domain validation:', {
                currentDomains: currentDomains,
                entered: enteredDomain,
                note: 'Custom domains may not be detected in browser. Server will validate.'
            });
            return true;
        }
        
        this.log(' Store URL validation passed (direct match):', enteredUrl);
        return true;
    }

    extractDomainFromUrl(url) {
        const clean = this.cleanUrlForComparison(url);
        
        if (!clean) {
            this.log(' Invalid store URL:', url);
            return null;
        }
        
        this.log(' Domain extracted:', clean);
        return clean;
    }

    async validateLicense(retryCount = 0) {
        if (this.hasImmediateStatus && this.immediateStatusApplied && 
            (this.licenseStatus === 'suspended' || this.licenseStatus === 'revoked')) {
            this.log(' Skipping validation - immediate suspended/revoked status should persist');
            return;
        }
        
        const currentLicenseKey = this.getLicenseFromSettings();
        const currentStoreUrl = this.getStoreUrlFromSettings();
        
        if (!currentLicenseKey || !currentStoreUrl) {
            this.log(' Missing fields:', {
                hasLicense: !!currentLicenseKey,
                hasStoreUrl: !!currentStoreUrl
            });
            
            if (window.Shopify?.designMode || window.location.href.includes('/customize')) {
                this.showCustomizerOverlay();
            }
            
            this.handleInvalidLicense('License key and store URL are required');
            return;
        }

        this.licenseKey = currentLicenseKey;
        this.storeUrl = currentStoreUrl;
        
        const userProvidedDomain = this.extractDomainFromUrl(currentStoreUrl);
        const shopPermanentDomain = (window.Shopify?.shop || window.themePermanentDomain || '').toLowerCase();
        const shopPrimaryDomain = (window.themePrimaryDomain || '').toLowerCase();
        
        // Security check: If user provides .myshopify.com domain, it must match actual shop domain
        if (userProvidedDomain && (shopPermanentDomain || shopPrimaryDomain)) {
            const isUserDomainShopify = userProvidedDomain.endsWith('.myshopify.com');
            
            if (isUserDomainShopify) {
                const matchesPrimary = shopPrimaryDomain && userProvidedDomain === shopPrimaryDomain;
                const matchesPermanent = shopPermanentDomain && userProvidedDomain === shopPermanentDomain;
                
                if (!matchesPrimary && !matchesPermanent) {
                    this.log('Shopify domain mismatch detected:', {
                        providedDomain: userProvidedDomain,
                        primaryDomain: shopPrimaryDomain,
                        permanentDomain: shopPermanentDomain
                    });
                    
                    if (window.Shopify?.designMode || window.location.href.includes('/customize')) {
                        this.showCustomizerOverlay();
                    }
                    
                    this.hideValidationLoading();
                    
                    const correctDomain = shopPrimaryDomain || shopPermanentDomain;
                    this.handleInvalidLicense({
                        error: `...Please use your actual Shopify domain: ${correctDomain}`,
                        require_domain_update: true,
                        licensed_domain: userProvidedDomain,
                        current_domain: correctDomain
                    });
                    
                    this.log(' Blocking server request due to Shopify domain mismatch');
                    return;
                }
            }
            
            if (isUserDomainShopify) {
                this.log('Shopify domain verified successfully (primary or permanent match):', userProvidedDomain);
            } else {
                this.log(' Custom domain provided - Server will verify:', userProvidedDomain);
            }
        }
        
        this.domain = userProvidedDomain;
        this.log('Proceeding with validation for domain:', this.domain);

        const cachedStatus = localStorage.getItem('licenseStatus');
        const cachedValid = localStorage.getItem('isLicenseValid');
        
        if (cachedStatus === 'suspended' || cachedStatus === 'revoked') {
            this.log(' Found cached suspended/revoked status - skipping server validation');
            this.log(' Cached Status:', { status: cachedStatus, valid: cachedValid });
            
            this.licenseStatus = cachedStatus;
            this.isValid = false;
            this.hasImmediateStatus = true;
            this.immediateStatusApplied = true;
            
            this.handleValidationResult({
                success: false,
                license_status: cachedStatus,
                message: cachedStatus === 'suspended' ? 'License is suspended' : 'License is revoked'
            });
            return;
        }
        
        this.log(' No cached restrictions - validating with server for real-time status');

        try {
            this.log('Making license validation request...');
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); 
            const response = await fetch(`${this.licenseServer}/api/license/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    license_key: this.licenseKey,
                    domain: this.domain
                }),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);

            if (!response.ok) {
                if (response.status === 403) {
                    this.log(' License access forbidden (403) - treating as suspended');
                    
                    const cachedStatus = localStorage.getItem('licenseStatus');
                    const statusToUse = (cachedStatus === 'revoked') ? 'revoked' : 'suspended';
                    
                    this.log(' Using status:', statusToUse);
                    
                    this.handleValidationResult({
                        success: false,
                        license_status: statusToUse,
                        message: statusToUse === 'suspended' ? 'License is suspended' : 'License is revoked'
                    });
                    return;
                }
                
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            this.log(' License validation response:', data);
            
            this.clearValidationCache();
            
            this.handleValidationResult(data);

        } catch (error) {
            this.logError('Validation request failed:', error.name === 'AbortError' ? 'Request timeout' : error.message);
            

            
            if (retryCount < this.retryAttempts) {
                this.log(` Retrying validation... (${retryCount + 1}/${this.retryAttempts})`);
                
                setTimeout(() => {
                    this.validateLicense(retryCount + 1);
                }, 1000 * (retryCount + 1)); 
            } else {

                
                const cachedStatus = localStorage.getItem('licenseStatus');
                const lastValidation = localStorage.getItem('lastSuccessfulValidation');
                const serverValidated = localStorage.getItem('serverValidatedTimestamp');
                const validatedLicenseKey = localStorage.getItem('validatedLicenseKey');
                const validatedStoreUrl = localStorage.getItem('validatedStoreUrl');
                const validatedShopDomain = localStorage.getItem('validatedShopDomain');
                
                const currentLicenseKey = this.getLicenseFromSettings();
                const currentStoreUrl = this.getStoreUrlFromSettings();
                const currentShopDomain = window.Shopify?.shop || window.themePermanentDomain || '';
                
                if (!currentLicenseKey || !currentStoreUrl) {

                    localStorage.removeItem('lastSuccessfulValidation');
                    localStorage.removeItem('serverValidatedTimestamp');
                    localStorage.removeItem('validatedLicenseKey');
                    localStorage.removeItem('validatedStoreUrl');
                    localStorage.removeItem('validatedShopDomain');
                    localStorage.removeItem('licenseStatus');
                    this.handleValidationError();
                    return;
                }
                
                if (validatedLicenseKey && currentLicenseKey !== validatedLicenseKey) {

                    localStorage.removeItem('lastSuccessfulValidation');
                    localStorage.removeItem('serverValidatedTimestamp');
                    localStorage.removeItem('validatedLicenseKey');
                    localStorage.removeItem('validatedStoreUrl');
                    localStorage.removeItem('validatedShopDomain');
                    localStorage.removeItem('licenseStatus');
                    this.handleValidationError();
                    return;
                }
                
                if (validatedShopDomain && currentShopDomain && validatedShopDomain !== currentShopDomain) {

                    localStorage.removeItem('lastSuccessfulValidation');
                    localStorage.removeItem('serverValidatedTimestamp');
                    localStorage.removeItem('validatedLicenseKey');
                    localStorage.removeItem('validatedStoreUrl');
                    localStorage.removeItem('validatedShopDomain');
                    localStorage.removeItem('licenseStatus');
                    this.handleValidationError();
                    return;
                }
                
                if (cachedStatus === 'suspended' || cachedStatus === 'revoked') {

                    this.handleValidationError();
                    return;
                }
                
                if (!lastValidation || !serverValidated) {

                    this.handleValidationError();
                    return;
                }
                
                const withinGracePeriod = this.checkGracePeriod();
                
                if (withinGracePeriod) {
                    this.isValid = true;
                    this.licenseStatus = 'active';
                    this.removeLicenseRestrictions();
                } else {

                    this.handleValidationError();
                }
            }
        }
    }

    handleValidationResult(data) {
        this.log(' Handling validation result:', data);
        
        if (this.hasImmediateStatus && this.immediateStatusApplied && 
            (this.licenseStatus === 'suspended' || this.licenseStatus === 'revoked')) {
            this.log(' Validation result ignored - immediate status takes precedence');
            return;
        }
        
        if (data.status === 'suspended' || data.status === 'revoked') {
            this.log(' License status detected:', data.status);
            this.licenseStatus = data.status;
            this.isValid = false;
            
            if (data.status === 'suspended') {
                this.log(' License is SUSPENDED (from status field)');
                
                localStorage.setItem('lastLicenseState', 'suspended');
                
            
                localStorage.removeItem('lastSuccessfulValidation');
                this.log(' Grace period timestamp removed - suspended license has no grace period');
                
                this.clearValidationCache();
                this.handleSuspendedLicense({
                    suspension_reason: data.error || 'License is suspended',
                    customer_portal_url: 'https://portal.surgetheme.com'
                });
                this.dispatchLicenseEvent('licenseSuspended', { status: 'suspended', data: data });
                return;
            } else if (data.status === 'revoked') {
                this.logError(' License is REVOKED (from status field)');
                
                localStorage.setItem('lastLicenseState', 'revoked');
                
               
                localStorage.removeItem('lastSuccessfulValidation');
                this.log(' Grace period timestamp removed - revoked license has no grace period');
                
                this.clearValidationCache();
                this.handleRevokedLicense({
                    revocation_reason: data.error || 'License is revoked',
                    customer_portal_url: 'https://portal.surgetheme.com'
                });
                this.dispatchLicenseEvent('licenseRevoked', { status: 'revoked', data: data });
                return;
            }
        }
        
        if (data.success) {
            const licenseStatus = data.license?.status || 'active';
            this.licenseStatus = licenseStatus;
            
            this.log(' License Status Check:', {
                success: data.success,
                status: licenseStatus,
                firstTime: data.first_time_capture || false
            });
            
            if (licenseStatus === 'active') {
                this.log(' License is ACTIVE - proceeding normally');
                
                this.startPeriodicCheck();
                
                localStorage.setItem('lastLicenseState', 'active');
                localStorage.setItem('licenseStatus', 'active');
                localStorage.setItem('isLicenseValid', 'true');
                localStorage.setItem('lastSuccessfulValidation', Date.now().toString());
                localStorage.setItem('serverValidatedTimestamp', Date.now().toString());
                
                localStorage.setItem('validatedLicenseKey', this.licenseKey);
                localStorage.setItem('validatedStoreUrl', this.storeUrl);
                
                const shopifyDomain = window.Shopify?.shop || window.themePermanentDomain || '';
                if (shopifyDomain) {
                    localStorage.setItem('validatedShopDomain', shopifyDomain);
                }
                

                
                
                const currentShopDomain = window.themePrimaryDomain || window.Shopify?.shop || window.location.hostname;
                localStorage.setItem('activatedStoreDomain', currentShopDomain);
                
                if (data.manual_domain_capture) {
                    this.log(' Manual domain captured successfully from theme settings');
                    this.log(' Domain registered for license:', this.domain);
                    
                    this.showManualCaptureSuccess(data);
                } else {
                    this.log(' Regular license validation successful');
                    this.log(' Licensed domain matches:', data.license?.domain || this.domain);
                    
                    if (this.settingsChanged) {
                        this.log(' Settings were changed - showing activation message');
                        this.showLicenseActivatedSuccess(data);
                        this.settingsChanged = false;
                    } else {
                        this.log(' No settings change - skipping activation message');
                    }
                }
                
                this.isValid = true;
                this.removeLicenseRestrictions();
                
                this.hasImmediateStatus = false;
                this.immediateStatusApplied = false;
                
                this.clearValidationCache();
                
                this.dispatchLicenseEvent('licenseValidated', { valid: true, data: data });
                
                this.log(' License system fully activated - buttons should work normally');
                
            } else if (licenseStatus === 'suspended') {
                this.log(' License is SUSPENDED');
                
                localStorage.setItem('lastLicenseState', 'suspended');
                
                
                localStorage.removeItem('lastSuccessfulValidation');
                localStorage.removeItem('lastActivatedLicense');
                
                this.isValid = false;
                
                this.clearValidationCache();
                
                this.handleSuspendedLicense(data);
                
                this.dispatchLicenseEvent('licenseSuspended', { status: 'suspended', data: data });
                
            } else if (licenseStatus === 'revoked') {
                this.logError(' License is REVOKED (Permanently Suspended)');
                
                localStorage.setItem('lastLicenseState', 'revoked');
                
               
                localStorage.removeItem('lastSuccessfulValidation');
                localStorage.removeItem('lastActivatedLicense');
                
                this.isValid = false;
                
                this.clearValidationCache();
                
                this.handleRevokedLicense(data);
                
                this.dispatchLicenseEvent('licenseRevoked', { status: 'revoked', data: data });
                
            } else {
                this.log(' Unknown license status:', licenseStatus);
                
                localStorage.setItem('lastLicenseState', 'invalid');
                
                localStorage.removeItem('lastActivatedLicense');
                
                this.isValid = false;
                this.handleInvalidLicense(data);
            }
            
        } else {
            this.log(' License validation failed:', data.error || 'Unknown error');
            
            localStorage.setItem('lastLicenseState', 'invalid');
            localStorage.setItem('licenseStatus', 'invalid');
            localStorage.setItem('isLicenseValid', 'false');
            
            this.isValid = false;
            this.licenseStatus = 'invalid';
            
            this.clearValidationCache();
            
            this.handleInvalidLicense(data);
            
            this.dispatchLicenseEvent('licenseValidated', { valid: false, error: data.error, data: data });
            
            this.log(' License restrictions applied due to validation failure');
        }
        
        this.log(' Final license state logged');
    }

    handleSuspendedLicense(data) {
        this.log(' Handling suspended license');
        
        this.licenseStatus = 'suspended';
        this.isValid = false;
        
        localStorage.setItem('licenseStatus', this.licenseStatus);
        localStorage.setItem('isLicenseValid', this.isValid.toString());
        
        this.applyLicenseRestrictions();
        
        this.showLicenseNotice();
        
        this.showSuspendedLicenseNotification(data);
    }

    handleRevokedLicense(data) {
        this.logError(' Handling revoked license');
        
        this.licenseStatus = 'revoked';
        this.isValid = false;
        
        localStorage.setItem('licenseStatus', this.licenseStatus);
        localStorage.setItem('isLicenseValid', this.isValid.toString());
        
        this.applyLicenseRestrictions();
        
        this.showLicenseNotice();
        
        this.showRevokedLicenseNotification(data);
    }

    showSuspendedLicenseNotification(data) {
        this.removeAllLicenseNotifications();
        
        const notification = document.createElement('div');
        notification.id = 'license-suspended-notification';
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #ff9800;
            color: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            z-index: 10000;
            max-width: 400px;
            font-size: 14px;
            line-height: 1.4;
        `;
        
        notification.innerHTML = `
            <div style="margin-bottom: 10px;">
                <strong> License Suspended</strong>
            </div>
            <div style="margin-bottom: 15px;">
                Your theme license has been temporarily suspended. 
                Shopping features are disabled until the license is restored.
            </div>
            <div style="margin-bottom: 15px;">
                <strong>Reason:</strong> ${data.suspension_reason || 'Violation of license terms'}
            </div>
            ${data.customer_portal_url ? `
            <div style="margin-bottom: 15px;">
                <a href="${data.customer_portal_url}" 
                   style="color: white; text-decoration: underline;" 
                   target="_blank">
                     Visit Customer Portal for Details
                </a>
            </div>
            ` : ''}
            <div style="text-align: right; margin-top: 10px;">
                <button onclick="document.getElementById('license-suspended-notification').remove()" 
                        style="background: rgba(255,255,255,0.3); color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer;">
                     Close
                </button>
            </div>
        `;
        
        document.body.appendChild(notification);
        
        setTimeout(() => {
            if (document.getElementById('license-suspended-notification')) {
                notification.remove();
            }
        }, 30000);
    }

    showRevokedLicenseNotification(data) {
        this.removeAllLicenseNotifications();
        
        const notification = document.createElement('div');
        notification.id = 'license-revoked-notification';
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #f44336;
            color: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            z-index: 10000;
            max-width: 400px;
            font-size: 14px;
            line-height: 1.4;
        `;
        
        notification.innerHTML = `
            <div style="margin-bottom: 10px;">
                <strong> License Revoked</strong>
            </div>
            <div style="margin-bottom: 15px;">
                Your theme license has been permanently revoked. 
                All theme features are disabled.
            </div>
            <div style="margin-bottom: 15px;">
                <strong>Reason:</strong> ${data.revocation_reason || 'Severe violation of license terms'}
            </div>
            ${data.customer_portal_url ? `
            <div style="margin-bottom: 15px;">
                <a href="${data.customer_portal_url}" 
                   style="color: white; text-decoration: underline;" 
                   target="_blank">
                     Contact Support for Resolution
                </a>
            </div>
            ` : ''}
            <div style="text-align: right; margin-top: 10px;">
                <button onclick="document.getElementById('license-revoked-notification').remove()" 
                        style="background: rgba(255,255,255,0.3); color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer;">
                     Close
                </button>
            </div>
        `;
        
        document.body.appendChild(notification);
        
    }

    showManualCaptureSuccess(data) {
        if (!window.Shopify?.designMode && 
            !window.location.href.includes('admin/themes') && 
            !document.body.classList.contains('admin')) {
            return;
        }
        
        this.removeAllLicenseNotifications();
        
        const notification = document.createElement('div');
        notification.id = 'license-capture-success';
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #28a745;
            color: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            z-index: 10000;
            max-width: 400px;
            font-size: 14px;
            line-height: 1.4;
        `;
        
        notification.innerHTML = `
            <div style="margin-bottom: 10px;">
                <strong> License Activated Successfully!</strong>
            </div>
            <div style="margin-bottom: 15px;">
                Your theme license has been activated for this store. 
                All features are now unlocked.
            </div>
            <div style="margin-bottom: 15px;">
                <strong>Domain:</strong> ${data.license?.domain || this.domain}
            </div>
            <div style="text-align: right; margin-top: 10px;">
                <button onclick="document.getElementById('license-capture-success').remove()" 
                        style="background: rgba(255,255,255,0.3); color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer;">
                     Close
                </button>
            </div>
        `;
        
        document.body.appendChild(notification);
        
        setTimeout(() => {
            if (document.getElementById('license-capture-success')) {
                notification.remove();
            }
        }, 10000);
    }

    showLicenseActivatedSuccess(data) {
        if (!window.Shopify?.designMode && 
            !window.location.href.includes('admin/themes') && 
            !document.body.classList.contains('admin')) {
            return;
        }
        
        this.removeAllLicenseNotifications();
        
        const notification = document.createElement('div');
        notification.id = 'license-activated-success';
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
            color: white;
            padding: 20px 25px;
            border-radius: 12px;
            box-shadow: 0 8px 16px rgba(0,0,0,0.2);
            z-index: 10000;
            max-width: 420px;
            font-size: 14px;
            line-height: 1.5;
            animation: slideInRight 0.5s ease-out;
        `;
        
        notification.innerHTML = `
            <div style="display: flex; align-items: center; margin-bottom: 12px;">
                <div style="width: 40px; height: 40px; background: rgba(255,255,255,0.3); border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-right: 15px; font-size: 24px;">
                    ?
                </div>
                <strong style="font-size: 18px;">License Activated Successfully!</strong>
            </div>
            <div style="margin-bottom: 12px; padding-left: 55px;">
                Your theme license has been activated for this store. 
                All features are now unlocked.
            </div>
            <div style="margin-bottom: 15px; padding-left: 55px; font-size: 13px; opacity: 0.9;">
                <strong>Domain:</strong> ${data.license?.domain || this.domain}
            </div>
            <div style="text-align: right; margin-top: 15px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.2);">
                <button onclick="document.getElementById('license-activated-success').remove()" 
                        style="background: rgba(255,255,255,0.2); color: white; border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-weight: 600; transition: all 0.3s;">
                     Close
                </button>
            </div>
        `;
        
        document.body.appendChild(notification);
        
        setTimeout(() => {
            if (document.getElementById('license-activated-success')) {
                notification.style.animation = 'slideOutRight 0.5s ease-out';
                setTimeout(() => {
                    notification.remove();
                }, 500);
            }
        }, 8000);
    }

    removeAllLicenseNotifications() {
        const notifications = [
            'license-domain-notification',
            'license-suspended-notification', 
            'license-revoked-notification',
            'license-capture-success',
            'license-activated-success',
            'license-notice'
        ];
        
        notifications.forEach(id => {
            const element = document.getElementById(id);
            if (element) {
                element.remove();
            }
        });
    }

    handleInvalidLicense(responseData) {
        const reason = responseData?.error || responseData || 'Unknown error';
        this.log(' License validation failed:', reason);
        this.log(' Response Data:', responseData);
        
        this.isValid = false;
        this.licenseStatus = 'invalid';
        
        localStorage.setItem('lastLicenseState', 'invalid');
        localStorage.setItem('licenseStatus', 'invalid');
        localStorage.setItem('isLicenseValid', 'false');
        
      
        localStorage.removeItem('lastSuccessfulValidation');
        localStorage.removeItem('serverValidatedTimestamp');
        localStorage.removeItem('lastActivatedLicense');
        localStorage.removeItem('validatedLicenseKey');
        localStorage.removeItem('validatedStoreUrl');
        localStorage.removeItem('validatedShopDomain');
        
        this.log(' Invalid license status saved for persistence');
        
        if (responseData.require_domain_update && responseData.customer_portal_url) {
            this.log(' Domain update required - redirecting to customer portal');
            
            this.showDomainMismatchNotification(responseData);
        } else {
            this.applyLicenseRestrictions();
        }
    }

    showDomainMismatchNotification(data) {
        const isHomepage = window.location.pathname === '/' || 
                          window.location.pathname === '' || 
                          window.location.pathname.includes('/pages/');

        const notification = document.createElement('div');
        notification.id = 'license-domain-notification';
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #ff6b6b;
            color: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            z-index: 10000;
            max-width: 400px;
            font-size: 14px;
            line-height: 1.4;
        `;
        
        const messageTitle = isHomepage ? 
            ' Store License Issue' : 
            ' License Domain Mismatch';
            
        const messageBody = isHomepage ?
            'This theme license is registered to a different domain. Shopping features may be restricted.' :
            `Licensed Domain: <strong>${data.licensed_domain}</strong><br>Current Domain: <strong>${data.current_domain}</strong>`;
        
        notification.innerHTML = `
            <div style="margin-bottom: 10px;">
                <strong>${messageTitle}</strong>
            </div>
            <div style="margin-bottom: 15px;">
                ${messageBody}
            </div>
            <div>
                <a href="${data.customer_portal_url}" 
                   style="color: white; text-decoration: underline;" 
                   target="_blank">
                     Update Domain in Customer Portal
                </a>
            </div>
            <div style="text-align: right; margin-top: 10px;">
                <button onclick="document.getElementById('license-domain-notification').remove()" 
                        style="background: rgba(255,255,255,0.3); color: white; border: none; padding: 5px 10px; border-radius: 4px; cursor: pointer;">
                     Close
                </button>
            </div>
        `;
        
        document.body.appendChild(notification);
        
        const autoRemoveTime = isHomepage ? 15000 : 10000;
        setTimeout(() => {
            if (document.getElementById('license-domain-notification')) {
                notification.remove();
            }
        }, autoRemoveTime);
        
        this.applyLicenseRestrictions();
    }

    handleValidationError() {
        localStorage.setItem('lastLicenseState', 'invalid');
        localStorage.setItem('licenseStatus', 'invalid');
        localStorage.setItem('isLicenseValid', 'false');
        
        
        
        this.isValid = false;
        this.licenseStatus = 'invalid';
        
        this.applyLicenseRestrictions();
        this.log(' License server connection failed - restrictions applied');
    }

    applyLicenseRestrictions() {
        this.hideValidationLoading();
        
        this.disablePurchaseButtons();
        
        this.addWatermark();
        
        this.showLicenseNotice();
       
        if (window.Shopify?.designMode || window.location.href.includes('/customize')) {
            this.showCustomizerOverlay();
        }
        
        document.body.classList.remove('theme-licensed');
        document.body.classList.add('theme-unlicensed');
        
        document.body.classList.remove('license-active', 'license-suspended', 'license-revoked');
        if (this.licenseStatus === 'suspended') {
            document.body.classList.add('license-suspended');
        } else if (this.licenseStatus === 'revoked') {
            document.body.classList.add('license-revoked');
        }
    }

    removeLicenseRestrictions() {
        this.hideValidationLoading();
        
        this.enablePurchaseButtons();
        
        this.removeWatermark();
        
        this.hideLicenseNotice();
        
        
        if (window.Shopify?.designMode || window.location.href.includes('/customize')) {
            this.hideCustomizerOverlay();
        }
        
        setTimeout(() => {
            this.enablePurchaseButtons();
        }, 1000);
        
        document.body.classList.remove('theme-unlicensed', 'license-suspended', 'license-revoked');
        document.body.classList.add('theme-licensed', 'license-active');
    }

    licenseClickHandler(e) {
        this.log(' License click handler triggered for status:', this.licenseStatus);
        
        if (!this.isValid) {
            e.preventDefault();
            e.stopPropagation();
            
            let message = 'VALID LICENSE REQUIRED TO PURCHASE';
            if (this.licenseStatus === 'suspended') {
                message = 'LICENSE SUSPENDED - PURCHASE DISABLED';
            } else if (this.licenseStatus === 'revoked') {
                message = 'LICENSE REVOKED - PURCHASE PERMANENTLY DISABLED';
            }
            
            this.showTooltip(e.target, message);
            return false;
        } else {
            this.log(' License is valid - allowing normal button behavior');
            return true;
        }
    }

    addWatermark() {
        if (!window.Shopify?.designMode && 
            !window.location.href.includes('admin/themes') && 
            !document.body.classList.contains('admin')) {
            return;
        }
        
        this.removeWatermark();
        
        const isHomepage = window.location.pathname === '/' || 
                          window.location.pathname === '' || 
                          window.location.pathname.includes('/pages/');
        
        const watermark = document.createElement('div');
        watermark.id = 'license-watermark';
        
        let watermarkText = '';
        let watermarkColor = 'rgba(255, 0, 0, 0.1)';
        
        if (this.licenseStatus === 'suspended') {
            watermarkText = isHomepage ? 'SUSPENDED' : 'SUSPENDED';
            watermarkColor = 'rgba(255, 152, 0, 0.15)';
        } else if (this.licenseStatus === 'revoked') {
            watermarkText = isHomepage ? 'PERMANENT SUSPENDED' : 'PERMANENT SUSPENDED';
            watermarkColor = 'rgba(255, 0, 0, 0.2)';
        } else {
            watermarkText = isHomepage ? '' : '';
        }
        
        watermark.innerHTML = watermarkText;
        
        const opacity = isHomepage ? '0.15' : '0.1';
        const fontSize = isHomepage ? '56px' : '48px';
        
        watermark.style.cssText = `
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-45deg);
            font-size: ${fontSize};
            font-weight: bold;
            color: ${watermarkColor};
            z-index: 999;
            pointer-events: none;
            user-select: none;
            font-family: Arial, sans-serif;
            letter-spacing: 10px;
        `;
        
        document.body.appendChild(watermark);
    }

    removeWatermark() {
        const watermark = document.getElementById('license-watermark');
        if (watermark) {
            watermark.remove();
        }
    }

    showLicenseNotice() {
        if (!window.Shopify?.designMode && 
            !window.location.href.includes('admin/themes') && 
            !document.body.classList.contains('admin')) {
            return;
        }
        
        let notice = document.getElementById('license-notice');
        let isExisting = !!notice;
        
        if (!notice) {
            notice = document.createElement('div');
            notice.id = 'license-notice';
        }
        
        let noticeText = ' THIS THEME REQUIRES A VALID LICENSE';
        let backgroundColor = '#ff4444';
        
        if (this.licenseStatus === 'suspended') {
            noticeText = ' SUSPENDED';
            backgroundColor = '#ff9800';
        } else if (this.licenseStatus === 'revoked') {
            noticeText = ' PERMANENT SUSPENDED';
            backgroundColor = '#f44336';
        }
        
        notice.innerHTML = `
            <div style="background: ${backgroundColor}; color: white; padding: 10px; text-align: center; position: fixed; top: 0; left: 0; right: 0; z-index: 1000; font-family: Arial, sans-serif;">
                <strong>${noticeText}</strong>
               
            </div>
        `;
        
        if (!isExisting) {
            document.body.appendChild(notice);
        }
        
        this.log(' License notice updated:', {
            status: this.licenseStatus,
            text: noticeText,
            backgroundColor: backgroundColor,
            existing: isExisting
        });
    }

    showTooltip(button, message) {
        const existingTooltip = document.getElementById('license-tooltip');
        if (existingTooltip) {
            existingTooltip.remove();
        }

        const tooltip = document.createElement('div');
        tooltip.id = 'license-tooltip';
        tooltip.textContent = message;
        tooltip.style.cssText = `
            position: absolute;
            background: #333;
            color: white;
            padding: 8px 12px;
            border-radius: 4px;
            font-size: 12px;
            z-index: 10000;
            pointer-events: none;
            white-space: nowrap;
            box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        `;

        const buttonRect = button.getBoundingClientRect();
        tooltip.style.left = (buttonRect.left + buttonRect.width / 2) + 'px';
        tooltip.style.top = (buttonRect.top - 40) + 'px';
        tooltip.style.transform = 'translateX(-50%)';

        document.body.appendChild(tooltip);

        setTimeout(() => {
            if (tooltip.parentNode) {
                tooltip.remove();
            }
        }, 3000);
    }

    hideLicenseNotice() {
        const notice = document.getElementById('license-notice');
        if (notice) {
            notice.remove();
        }
        
        const tooltip = document.getElementById('license-tooltip');
        if (tooltip) {
            tooltip.remove();
        }
    }

    showLicensePrompt() {
        const modal = this.createLicenseModal();
        modal.innerHTML = `
            <div style="background: white; padding: 30px; border-radius: 10px; max-width: 500px; margin: 0 auto;">
                <h2 style="margin-bottom: 20px; color: #333;">Enter License Key</h2>
                <p style="margin-bottom: 20px; color: #666;">Please enter your theme license key to activate this theme.</p>
                <input type="text" id="license-input" placeholder="Enter your license key" style="width: 100%; padding: 10px; margin-bottom: 15px; border: 1px solid #ddd; border-radius: 5px;">
                <div style="text-align: right;">
                    <button onclick="this.validateInputLicense()" style="background: #007cba; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer;">Activate</button>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
    }

    showLicenseModal() {
        const modal = this.createLicenseModal();
        modal.innerHTML = `
            <div style="background: white; padding: 30px; border-radius: 10px; max-width: 500px; margin: 0 auto;">
                <h2 style="margin-bottom: 20px; color: #333;">LICENSE REQUIRED</h2>
                <p style="margin-bottom: 20px; color: #666;">This theme requires a valid license to enable shopping functionality.</p>
                <p style="margin-bottom: 20px; color: #666;">Please contact the store owner to resolve this issue.</p>
                <div style="text-align: right;">
                    <button onclick="this.closeLicenseModal()" style="background: #007cba; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer;">Close</button>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
    }

    showLicenseWarning(reason) {
        const modal = this.createLicenseModal();
        modal.innerHTML = `
            <div style="background: white; padding: 30px; border-radius: 10px; max-width: 500px; margin: 0 auto;">
                <h2 style="margin-bottom: 20px; color: #d32f2f;">License Issue</h2>
                <p style="margin-bottom: 20px; color: #666;">${reason}</p>
                <p style="margin-bottom: 20px; color: #666;">Please contact support for assistance.</p>
                <div style="text-align: right;">
                    <button onclick="this.closeLicenseModal()" style="background: #d32f2f; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer;">Close</button>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
    }

    showServerError() {
        const modal = this.createLicenseModal();
        modal.innerHTML = `
            <div style="background: white; padding: 30px; border-radius: 10px; max-width: 500px; margin: 0 auto;">
                <h2 style="margin-bottom: 20px; color: #ff9800;">Connection Error</h2>
                <p style="margin-bottom: 20px; color: #666;">Unable to verify license. Please check your internet connection.</p>
                <div style="text-align: right;">
                    <button onclick="this.retryValidation()" style="background: #ff9800; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer; margin-right: 10px;">Retry</button>
                    <button onclick="this.closeLicenseModal()" style="background: #666; color: white; border: none; padding: 10px 20px; border-radius: 5px; cursor: pointer;">Close</button>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
    }

    createLicenseModal() {
        this.closeLicenseModal();
        
        const modal = document.createElement('div');
        modal.id = 'license-modal';
        modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.7);
            z-index: 10000;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: Arial, sans-serif;
        `;
        
        return modal;
    }

    closeLicenseModal() {
        const modal = document.getElementById('license-modal');
        if (modal) {
            modal.remove();
        }
    }

    validateInputLicense() {
        const input = document.getElementById('license-input');
        const licenseKey = input.value.trim();
        
        if (licenseKey) {
            this.licenseKey = licenseKey;
            this.closeLicenseModal();
            this.validateLicense();
            
            this.saveLicenseToSettings(licenseKey);
        }
    }

    saveLicenseToSettings(licenseKey) {
        localStorage.setItem('themeLicenseKey', licenseKey);
        window.themeLicenseKey = licenseKey;
    }

    retryValidation() {
        this.closeLicenseModal();
        this.validateLicense();
    }

    startPeriodicCheck() {
        setInterval(() => {
            this.validateLicense();
        }, this.checkInterval);
    }

    cacheValidation(data) {
        const cacheData = {
            data: data,
            timestamp: Date.now(),
            domain: this.domain,
            licenseKey: this.licenseKey,
            status: this.licenseStatus,
            isValid: this.isValid
        };
        localStorage.setItem('licenseValidationCache', JSON.stringify(cacheData));
        
        localStorage.setItem('isLicenseValid', this.isValid.toString());
        localStorage.setItem('licenseStatus', this.licenseStatus);
    }

    getCachedValidation() {
        try {
            const cached = localStorage.getItem('licenseValidationCache');
            if (!cached) return null;
            
            const cacheData = JSON.parse(cached);
            
            if (cacheData.domain !== this.domain || cacheData.licenseKey !== this.licenseKey) {
                this.clearValidationCache();
                return null;
            }
            
            return cacheData;
        } catch (error) {
            this.clearValidationCache();
            return null;
        }
    }

    clearValidationCache() {
        localStorage.removeItem('licenseValidationCache');
        this.log(' License validation cache cleared but status/validity preserved for consistency');
    }
    
    showDomainConfirmationForActivation(data) {
        this.log(' Showing domain confirmation after first activation');
        
        const detectedDomain = data.license?.domain || this.domain;
        const primaryDomain = window.themePrimaryDomain || '';
        const permanentDomain = window.themePermanentDomain || '';
        const currentDomain = window.location.hostname;
        const isCustomDomain = !currentDomain.endsWith('.myshopify.com');
        
        this.removeAllLicenseNotifications();
        
        const modal = document.createElement('div');
        modal.id = 'domain-activation-confirmation';
        modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: rgba(0, 0, 0, 0.8);
            z-index: 999999;
            display: flex;
            align-items: center;
            justify-content: center;
            animation: fadeIn 0.3s ease-out;
        `;
        
        const modalContent = document.createElement('div');
        modalContent.style.cssText = `
            background: white;
            border-radius: 16px;
            padding: 40px;
            max-width: 550px;
            width: 90%;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
            animation: slideUp 0.4s ease-out;
        `;
        
        const domainOptions = [];
        
        if (detectedDomain) {
            domainOptions.push({
                value: detectedDomain,
                label: detectedDomain,
                description: detectedDomain.endsWith('.myshopify.com') ? 'Shopify Store Domain (Auto-captured)' : 'Custom Domain (Auto-captured)',
                recommended: true
            });
        }
        
        if (isCustomDomain && currentDomain !== detectedDomain) {
            domainOptions.push({
                value: currentDomain,
                label: currentDomain,
                description: 'Current Custom Domain',
                recommended: true
            });
        }
        
        if (primaryDomain && primaryDomain !== detectedDomain && primaryDomain !== currentDomain) {
            domainOptions.push({
                value: primaryDomain,
                label: primaryDomain,
                description: 'Shopify Primary Domain'
            });
        }
        
        if (permanentDomain && permanentDomain !== detectedDomain && permanentDomain !== primaryDomain && permanentDomain !== currentDomain) {
            domainOptions.push({
                value: permanentDomain,
                label: permanentDomain,
                description: 'Shopify Permanent Domain'
            });
        }
        
        let optionsHTML = '';
        domainOptions.forEach((option, index) => {
            const isRecommended = option.recommended;
            optionsHTML += `
                <label style="display: block; padding: 16px; margin-bottom: 12px; border: 2px solid ${isRecommended ? '#04A9EC' : '#E5E7EB'}; border-radius: 10px; cursor: pointer; transition: all 0.3s; background: ${isRecommended ? '#F0F9FF' : 'white'};">
                    <input type="radio" name="domain_confirm" value="${option.value}" ${index === 0 ? 'checked' : ''} style="margin-right: 12px;">
                    <span style="font-weight: 600; color: #1C2638; font-size: 15px;">${option.label}</span>
                    ${isRecommended ? '<span style="background: #04A9EC; color: white; padding: 2px 8px; border-radius: 4px; font-size: 11px; margin-left: 8px; font-weight: 700;">ACTIVATED</span>' : ''}
                    <div style="margin-left: 28px; margin-top: 4px; font-size: 13px; color: #6B7280;">${option.description}</div>
                </label>
            `;
        });
        
        modalContent.innerHTML = `
            <div style="text-align: center; margin-bottom: 30px;">
                <div style="width: 70px; height: 70px; background: linear-gradient(135deg, #28a745 0%, #20c997 100%); border-radius: 50%; margin: 0 auto 20px; display: flex; align-items: center; justify-content: center; font-size: 36px; color: white;">
                    ✓
                </div>
                <h2 style="font-size: 26px; font-weight: 700; color: #1C2638; margin: 0 0 12px 0;">License Activated Successfully!</h2>
                <p style="font-size: 15px; color: #6B7280; margin: 0;">Your license has been activated with the following domain</p>
            </div>
            
            <div style="background: #F0F9FF; border-left: 4px solid #04A9EC; padding: 16px; margin-bottom: 24px; border-radius: 8px;">
                <div style="display: flex; align-items: start; gap: 12px;">
                    <span style="font-size: 20px;">ℹ️</span>
                    <div>
                        <strong style="color: #0284c7; font-size: 14px;">Domain Registered:</strong>
                        <p style="margin: 8px 0 0 0; font-size: 13px; color: #0284c7; line-height: 1.5;">
                            ${isCustomDomain ? 
                                `Your license is now activated with <strong>${detectedDomain}</strong>. If you want to use a different domain, please contact support.` : 
                                `Your license is activated for your Shopify store domain.`}
                        </p>
                    </div>
                </div>
            </div>
            
            <div id="domain-confirm-options" style="margin-bottom: 24px;">
                ${optionsHTML}
            </div>
            
            <div style="display: flex; gap: 12px; justify-content: center;">
                <button id="domain-confirm-ok-btn" style="padding: 12px 40px; border: none; background: linear-gradient(135deg, #28a745 0%, #20c997 100%); color: white; border-radius: 8px; font-weight: 600; cursor: pointer; font-size: 14px; transition: all 0.3s; box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);">
                    Continue
                </button>
            </div>
            
            <style>
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from { transform: translateY(30px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                #domain-confirm-options label:hover {
                    border-color: #04A9EC !important;
                    background: #F0F9FF !important;
                }
                #domain-confirm-ok-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(40, 167, 69, 0.4);
                }
            </style>
        `;
        
        modal.appendChild(modalContent);
        document.body.appendChild(modal);
        
        const confirmBtn = document.getElementById('domain-confirm-ok-btn');
        
        confirmBtn.addEventListener('click', () => {
            modal.remove();
            
            this.isValid = true;
            this.licenseStatus = 'active';
            this.removeLicenseRestrictions();
            
            localStorage.setItem('licenseStatus', 'active');
            localStorage.setItem('isLicenseValid', 'true');
            localStorage.setItem('lastActivatedLicense', this.licenseKey);
        });
    }

    isLicenseValid() {
        return this.isValid;
    }

    getLicenseStatus() {
        return {
            valid: this.isValid,
            status: this.licenseStatus,
            key: this.licenseKey,
            domain: this.domain
        };
    }

    checkGracePeriod() {
        const lastSuccessfulValidation = localStorage.getItem('lastSuccessfulValidation');
        
        if (!lastSuccessfulValidation) {
            this.log(' No previous successful validation found');
            return false;
        }
        
        const lastValidationTime = parseInt(lastSuccessfulValidation);
        const currentTime = Date.now();
        const gracePeriodDays = 7;
        const gracePeriodMs = gracePeriodDays * 24 * 60 * 60 * 1000;
        
        const timeSinceLastValidation = currentTime - lastValidationTime;
        
        if (timeSinceLastValidation <= gracePeriodMs) {
            const daysRemaining = Math.ceil((gracePeriodMs - timeSinceLastValidation) / (24 * 60 * 60 * 1000));
            this.log(` Within grace period - ${daysRemaining} days remaining`);
            
            if (window.Shopify?.designMode || window.location.href.includes('/customize')) {
                console.log(`Grace ${daysRemaining}/7`);
            }
            
            return true;
        }
        
        this.log(' Grace period expired');
        return false;
    }

    dispatchLicenseEvent(eventName, detail) {
        const event = new CustomEvent(eventName, { 
            detail: detail,
            bubbles: true,
            cancelable: true
        });
        
        window.dispatchEvent(event);
        
        document.dispatchEvent(event);
    }
    
    refreshRestrictions() {
        if (this.isValid) {
            this.removeLicenseRestrictions();
        } else {
            this.applyLicenseRestrictions();
        }
    }
}

document.addEventListener('DOMContentLoaded', function() {
    try {
        
        if (!document.getElementById('license-notification-styles')) {
            const style = document.createElement('style');
            style.id = 'license-notification-styles';
            style.textContent = `
                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(100%);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
                
                @keyframes slideOutRight {
                    from {
                        opacity: 1;
                        transform: translateX(0);
                    }
                    to {
                        opacity: 0;
                        transform: translateX(100%);
                    }
                }
                
                #license-activated-success button:hover {
                    background: rgba(255,255,255,0.3) !important;
                    transform: scale(1.05);
                }
            `;
            document.head.appendChild(style);
        }
        
        const licenseConfig = {
            licenseServer: window.THEME_LICENSE_CONFIG?.serverUrl || window.licenseServerUrl || 'https://portal.surgetheme.com',
            checkInterval: 24 * 60 * 60 * 1000, 
            retryAttempts: 3,
            cacheDuration: 0 
        };
        
        window.licenseValidator = new ShopifyLicenseValidator(licenseConfig);
        
        const immediateStatus = localStorage.getItem('licenseStatus');
        const immediateValidFlag = localStorage.getItem('isLicenseValid');
        
        if ((immediateStatus === 'suspended' || immediateStatus === 'revoked') && immediateValidFlag === 'false') {
            this.log(' APPLYING IMMEDIATE RESTRICTIONS for status:', immediateStatus);
            
            if (window.licenseValidator) {
                window.licenseValidator.licenseStatus = immediateStatus;
                window.licenseValidator.isValid = false;
                
                window.licenseValidator.applyLicenseRestrictions();
                
                window.licenseValidator.hasImmediateStatus = true;
                window.licenseValidator.immediateStatusApplied = true;
                
                this.log(' Immediate restrictions applied successfully');
            }
            
            setTimeout(() => {
                if (window.licenseValidator && !window.licenseValidator.isValid) {
                    window.licenseValidator.licenseStatus = immediateStatus;
                    window.licenseValidator.isValid = false;
                    window.licenseValidator.applyLicenseRestrictions();
                    this.log(' Backup restrictions applied');
                }
            }, 50);
        }
        
    } catch (initError) {
    }
});


if (typeof module !== 'undefined' && module.exports) {
    module.exports = ShopifyLicenseValidator;
}